import { format, formatDistance, differenceInMonths, differenceInDays, addMonths, getDaysInMonth } from 'date-fns'

/* ABSENCE IS A VALUE HERE, AND IT IS NOT A DATE.

   `new Date(null)` is not an Invalid Date -- null numeric-coerces to 0, so it is a
   perfectly VALID Date sitting on the Unix epoch. Nothing throws, so the try/catch below
   never fires, and the caller is handed "1 January 1970" as though a date had been
   recorded. `undefined` and '' do produce an Invalid Date and so returned '' by accident;
   the two falsy values that coerce instead silently invented a date.

   That reached a contractual surface. The Schedule 1 preview rendered "Effective Date:
   1 January 1970" for an introducer with no agreement, and then built a sentence on it.
   On a page clause 6.10 turns into written notification, an invented date is not a
   formatting slip.

   So absence is detected BEFORE the coercion and returned as its own value, never as a
   Date. Every caller of formatDate is covered by this, not just the one that was caught. */

const ABSENT = Symbol('absent-date')

function utc(date) {
  /* `typeof` rather than the `undefined` literal, which this repo's eslint forbids. */

  if (date === null || typeof date === 'undefined' || date === '' || date === 0) {
    return ABSENT
  }

  const parsed = new Date(date)

  if (isNaN(parsed.getTime())) {
    return ABSENT
  }

  /* An epoch-0 that survived parsing is an artefact, never a fact: this platform records
     nothing from 1970, so a value landing exactly on the epoch came from a coercion
     upstream -- a stored `new Date(0)`, or a 0 read as a timestamp. Rendering it would
     restate the same false claim in a different colour. If a genuine 1 January 1970 ever
     needs displaying, this is the line to revisit, deliberately. */

  if (parsed.getTime() === 0) {
    return ABSENT
  }

  return new Date(parsed.toUTCString().slice(0, -3))
}

/* `absent` is what the caller wants an absence to READ as. It defaults to '', which is
   what undefined and '' already produced, so no existing call site changes meaning --
   only the null and epoch cases stop inventing a date. A caller whose surface would
   misread a blank should pass something explicit instead. */

export function formatDate(date, string = 'dd/MM/yyyy', absent = '') {
  const parsed = utc(date)

  if (parsed === ABSENT) {
    return absent
  }

  try {
    return format(parsed, string)
  } catch (e) {
    return absent
  }
}

/* True when `date` is something this module would actually render. Exported so a view can
   branch on the absence itself rather than testing the formatted output for emptiness --
   a template that must say "no date was recorded" needs the question, not the answer. */

export function hasDate(date) {
  return utc(date) !== ABSENT
}

export function formatRelativeDate(from) {
  /* Same trap, same fix: formatDistance(new Date(null), now) reads "56 years ago". */

  if (!hasDate(from)) {
    return ''
  }

  try {
    return formatDistance(new Date(from), new Date(), { addSuffix: true })
  } catch (e) {
    return ''
  }
}

/* Pluralise on the count, so "1 year 1 month" rather than "1 years 1 months". */

function plural(n, unit) {
  return n + ' ' + unit + (n === 1 ? '' : 's')
}

/* TENURE IN YEARS AND MONTHS -- a DIFFERENT QUESTION from formatRelativeDate, which is
   why it is a different function rather than a change to that one.

   formatRelativeDate answers "how long ago did this happen", and date-fns formatDistance
   answers it in one unit with a qualifier: over 1 year, about 5 years, almost 7 years.
   That is right for a freshness stamp -- AllocationCard uses it on wallet_composition
   and position_status, where the answer is minutes old and a second unit would be noise.
   It is wrong for tenure: "over 1 year" on a 21-month relationship understates it, and on
   a six-and-a-half year account it silently discards six months of standing.

   Left deliberately for anything client-facing that wants the coarse phrasing: on an
   invoice, "1 year 9 months" would read as a precision the document does not intend.
   Two questions, two formatters -- do not merge them.

   ROUNDED TO THE NEAREST MONTH, not truncated. 12 December 2024 to 8 September 2026 is
   20 complete months and 27 days; this reports 1 year 9 months, because 27 days of a
   30-day month is a month to anyone reading a tenure figure. Truncation would say 1 year
   8 months. Flip the comparison below to change that -- it is the one judgement here.

   Calendar months via date-fns, never milliseconds / 30.44: month lengths differ, and an
   account funded on the 31st has no anniversary in most months. differenceInMonths
   handles both. */

export function formatTenure(from) {
  if (!hasDate(from)) {
    return ''
  }

  try {
    const start = new Date(from)
    const now = new Date()

    let months = differenceInMonths(now, start)

    if (months < 0) {
      months = 0
    }

    const anniversary = addMonths(start, months)

    if (differenceInDays(now, anniversary) >= getDaysInMonth(anniversary) / 2) {
      months += 1
    }

    /* UNDER A MONTH SAYS SO, rather than "0 months", which reads as a failed render
       rather than a young account. No client is in this state today (measured
       2026-09-08: the newest funded start is 2 months), but one funded this week is. */

    if (months < 1) {
      return 'less than a month'
    }

    const years = Math.floor(months / 12)
    const remainder = months % 12

    if (years === 0) {
      return plural(remainder, 'month')
    }

    /* An exact anniversary reads "6 years", never "6 years 0 months". Ten clients sit
       on one today. */

    if (remainder === 0) {
      return plural(years, 'year')
    }

    return plural(years, 'year') + ' ' + plural(remainder, 'month')
  } catch (e) {
    return ''
  }
}

export function formatDuration(ms) {
  const from = new Date(ms).getTime() / 1000
  const to = new Date().getTime() / 1000

  const duration = to - from

  let delta = Math.abs(duration)

  const days = Math.floor(delta / 86400)

  delta -= days * 86400

  const hours = Math.floor(delta / 3600) % 24

  delta -= hours * 3600

  const minutes = Math.floor(delta / 60) % 60

  delta -= minutes * 60

  const seconds = Math.floor(delta % 60)

  const time = hours + ':' + (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds)

  return (days ? days + 'd ' : '') + time
}
